import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
    FaClipboardList,
    FaPlus,
    FaSync,
    FaUsers
} from 'react-icons/fa/index.js';
import callOysterApi from '../../utils/call-oyster-api.js';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import CreateTaskModal from '../../components/tasks/CreateTaskModal.jsx';
import '../../styles/TeamTasksPage.css';
import useCurrentUser from '../../hooks/useCurrentUser.js';

// ---- NEW SUBCOMPONENTS ----
import Sidebar from './Sidebar.jsx';
import StatsDashboard from './StatsDashboard.jsx';
import UserTasksView from './UserTasksView.jsx';

function teamsMatch(a, b) {
    const x = (a ?? '').toString().trim().toLowerCase();
    const y = (b ?? '').toString().trim().toLowerCase();
    return x.length > 0 && x === y;
}

const TeamTasksPage = () => {
    const { user, isTeamLead } = useCurrentUser();
    const [refreshing, setRefreshing] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    // Initialize state from URL params (survives page refresh)
    const getInitialView = () => {
        const type = searchParams.get('view');
        if (type === 'team' && searchParams.get('id')) {
            return { type: 'team', id: searchParams.get('id') };
        }
        if (type === 'user' && searchParams.get('email')) {
            return {
                type: 'user',
                email: searchParams.get('email'),
                name: searchParams.get('name') || ''
            };
        }
        return { type: 'all' };
    };

    // Core Layout State
    // view can be: { type: 'all' } | { type: 'team', id: 'Marketing' } | { type: 'user', email: '...', name: '...' }
    const [selectedView, setSelectedViewState] = useState(getInitialView);
    const initialQuarter = searchParams.get('quarter') || '';
    const [selectedQuarter, setSelectedQuarterState] = useState(initialQuarter);
    const initialWeek = searchParams.get('week') || 'all';
    const [selectedWeek, setSelectedWeekState] = useState(initialWeek);
    const [sidebarData, setSidebarData] = useState([]);

    // Wrap setters to sync URL params
    const setSelectedView = useCallback(
        (view) => {
            setSelectedViewState(view);
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('view', view.type);
                    if (view.type === 'team' && view.id) {
                        next.set('id', view.id);
                        next.delete('email');
                        next.delete('name');
                    } else if (view.type === 'user' && view.email) {
                        next.set('email', view.email);
                        next.set('name', view.name || '');
                        next.delete('id');
                    } else {
                        next.delete('id');
                        next.delete('email');
                        next.delete('name');
                    }
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const setSelectedQuarter = useCallback(
        (quarter) => {
            setSelectedQuarterState(quarter);
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('quarter', quarter);
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const setSelectedWeek = useCallback(
        (weekKey) => {
            setSelectedWeekState(weekKey);
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (!weekKey || weekKey === 'all') {
                        next.delete('week');
                    } else {
                        next.set('week', weekKey);
                    }
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    // Global Task State
    const [allTasks, setAllTasks] = useState([]);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [tasksRefreshTrigger, setTasksRefreshTrigger] = useState(0);
    const [isLoadingSidebar, setIsLoadingSidebar] = useState(false);
    /** API returned 403 — show a neutral notice, not an error toast */
    const [dashboardTasksForbidden, setDashboardTasksForbidden] =
        useState(false);

    // Modals
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [showTasks, setShowTasks] = useState(false);

    const isSuperAdmin = user?.isItLeadership;
    const hasManagementRights = user?.isTeamLead || user?.isItLeadership;
    const isTeamMember = !!(user?.hdTeam && String(user.hdTeam).trim());

    // Determine if the "Show Tasks" toggle should be visible for this view
    const canSeeTasks = React.useMemo(() => {
        if (selectedView.type === 'user') {
            if (selectedView.email === user?.email) return true;
        }

        if (isSuperAdmin) return true;

        if (hasManagementRights) {
            const userTeam = user?.hdTeam;
            if (!userTeam) return false;

            if (selectedView.type === 'all') return true;
            if (selectedView.type === 'team')
                return teamsMatch(selectedView.id, userTeam);
            if (selectedView.type === 'user') {
                const teamGroup = sidebarData.find((g) =>
                    teamsMatch(g.team, userTeam)
                );
                return (
                    teamGroup?.users?.some(
                        (u) => u.email === selectedView.email
                    ) ?? false
                );
            }
            return false;
        }

        // Regular team member: API returns only their team’s tasks; mirror that in the UI
        if (isTeamMember) {
            const userTeam = user?.hdTeam;
            if (selectedView.type === 'all') return true;
            if (selectedView.type === 'team')
                return teamsMatch(selectedView.id, userTeam);
            if (selectedView.type === 'user') {
                const teamGroup = sidebarData.find((g) =>
                    teamsMatch(g.team, userTeam)
                );
                return (
                    teamGroup?.users?.some(
                        (u) => u.email === selectedView.email
                    ) ?? false
                );
            }
        }

        return false;
    }, [
        isSuperAdmin,
        hasManagementRights,
        isTeamMember,
        user,
        selectedView,
        sidebarData
    ]);

    // 1. Fetch available Quarters
    const availableQuarters = React.useMemo(() => {
        return Array.from({ length: 8 }).map((_, i) => {
            const d = new Date();
            d.setMonth(d.getMonth() - 3 * i);
            const y = d.getFullYear();
            const q = Math.floor(d.getMonth() / 3) + 1;
            return `${y}-Q${q}`;
        });
    }, []);

    const availableWeeks = React.useMemo(() => {
        if (!selectedQuarter) return [];
        const quarterMatch = selectedQuarter.match(/^(\d{4})-Q([1-4])$/);
        if (!quarterMatch) return [];

        const year = Number(quarterMatch[1]);
        const quarter = Number(quarterMatch[2]);
        const startMonth = (quarter - 1) * 3;

        const quarterStart = new Date(Date.UTC(year, startMonth, 1));
        const quarterEnd = new Date(Date.UTC(year, startMonth + 3, 1));

        const options = [];
        const cursor = new Date(quarterStart);
        let weekIndex = 1;

        while (cursor < quarterEnd) {
            const weekStart = new Date(cursor);
            const weekEnd = new Date(cursor);
            weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);
            if (weekEnd >= quarterEnd) {
                weekEnd.setTime(quarterEnd.getTime());
                weekEnd.setUTCDate(weekEnd.getUTCDate() - 1);
            }

            const startYmd = weekStart.toISOString().split('T')[0];
            const endYmd = weekEnd.toISOString().split('T')[0];
            const key = `${startYmd}_${endYmd}`;

            const label = `Week ${weekIndex} (${weekStart.toLocaleDateString(
                'en-US',
                { month: 'short', day: 'numeric' }
            )} - ${weekEnd.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            })})`;

            options.push({
                key,
                label,
                start: startYmd,
                end: endYmd
            });

            cursor.setUTCDate(cursor.getUTCDate() + 7);
            weekIndex += 1;
        }

        return options;
    }, [selectedQuarter]);

    const selectedWeekRange = React.useMemo(() => {
        if (!selectedWeek || selectedWeek === 'all') return null;
        const selected = availableWeeks.find((w) => w.key === selectedWeek);
        if (!selected) return null;
        return {
            start: selected.start,
            end: selected.end
        };
    }, [availableWeeks, selectedWeek]);

    const selectedWeekLabel = React.useMemo(() => {
        if (!selectedWeek || selectedWeek === 'all') return 'All Weeks';
        const selected = availableWeeks.find((w) => w.key === selectedWeek);
        return selected?.label || 'All Weeks';
    }, [availableWeeks, selectedWeek]);

    // Fix #5: Validate quarter param — fall back to current if invalid
    useEffect(() => {
        if (!selectedQuarter || !availableQuarters.includes(selectedQuarter)) {
            setSelectedQuarter(availableQuarters[0]);
        }
    }, [availableQuarters, selectedQuarter, setSelectedQuarter]);

    useEffect(() => {
        if (!selectedWeek || selectedWeek === 'all') return;
        const weekExists = availableWeeks.some((w) => w.key === selectedWeek);
        if (!weekExists) {
            setSelectedWeek('all');
        }
    }, [availableWeeks, selectedWeek, setSelectedWeek]);

    // Fix #4: Validate URL view params against sidebar data after it loads
    useEffect(() => {
        if (sidebarData.length === 0) return; // Still loading
        if (selectedView.type === 'team') {
            const teamExists = sidebarData.some(
                (g) => g.team === selectedView.id
            );
            if (!teamExists) {
                setSelectedView({ type: 'all' });
            }
        } else if (selectedView.type === 'user') {
            const userExists = sidebarData.some((g) =>
                (g.users || []).some((u) => u.email === selectedView.email)
            );
            if (!userExists) {
                setSelectedView({ type: 'all' });
            }
        }
    }, [sidebarData]); // Only run when sidebar data loads

    // 2. Fetch Sidebar Users group by Team (curated six-team Tasks roster —
    // see oracle-backend src/models/TasksTeamMember.js, seeded via
    // scripts/seed-tasks-team-roster.js. Not the same source as
    // TaskReportPage/AuditTicketsForm, which still use the unfiltered
    // /dashboard/team-users list.)
    const fetchSidebarData = async () => {
        try {
            setIsLoadingSidebar(true);
            const teamUsersRes = await callOysterApi(
                '/dashboard/tasks-team-roster',
                'GET'
            );
            if (teamUsersRes.status === 403) {
                setSidebarData([]);
                return;
            }
            if (teamUsersRes.ok) {
                const data = await teamUsersRes.json();
                setSidebarData(data.payload || []);
            } else {
                toast.error('Failed to load teams for sidebar');
            }
        } catch (error) {
            console.error('Error fetching sidebar teams:', error);
            toast.error('Error loading sidebar');
        } finally {
            setIsLoadingSidebar(false);
        }
    };

    useEffect(() => {
        fetchSidebarData();
    }, [hasManagementRights, isSuperAdmin]);

    // 2.5 Fetch ALL Tasks Globally for the Quarter
    const hasLoadedTasks = React.useRef(false);

    // Reset initial-load flag when quarter changes
    useEffect(() => {
        hasLoadedTasks.current = false;
    }, [selectedQuarter]);

    // Fix #6: Cancelled-ref prevents stale responses from fast quarter switching
    useEffect(() => {
        if (!selectedQuarter) return;

        let cancelled = false;
        const isInitialLoad = !hasLoadedTasks.current;

        const fetchAllTasks = async () => {
            // Only show full-page spinner on initial load, not background refreshes
            if (isInitialLoad) setLoadingTasks(true);
            try {
                // Fetch page size up to 1000 so we get all company tasks
                const res = await callOysterApi(
                    `/dashboard/tasks?quarter=${selectedQuarter}&page=1&pageSize=1000`,
                    'GET'
                );
                if (cancelled) return;
                if (res.ok) {
                    setDashboardTasksForbidden(false);
                    const data = await res.json();
                    setAllTasks(data.payload || []);
                    hasLoadedTasks.current = true;
                } else if (res.status === 403) {
                    setDashboardTasksForbidden(true);
                    setAllTasks([]);
                    hasLoadedTasks.current = true;
                } else {
                    setDashboardTasksForbidden(false);
                    toast.error('Failed to load global tasks');
                }
            } catch (error) {
                if (cancelled) return;
                console.error('Error fetching global tasks:', error);
                toast.error('Error fetching task data');
            } finally {
                if (!cancelled) {
                    setLoadingTasks(false);
                }
            }
        };
        fetchAllTasks();

        return () => {
            cancelled = true;
        };
    }, [selectedQuarter, tasksRefreshTrigger]);

    // 3. Auto-sync on load
    const hasSyncedOnLoad = React.useRef(false);
    useEffect(() => {
        if (user && !hasSyncedOnLoad.current) {
            hasSyncedOnLoad.current = true;
            callOysterApi('/sync', 'POST')
                .then((res) => {
                    if (res.status === 403) return;
                    if (res.ok) {
                        return res.json().then((data) => {
                            if (data.payload?.reconciled_count > 0) {
                                toast.success(
                                    `Reconciled ${data.payload.reconciled_count} task(s)`
                                );
                            }
                            // Refresh tasks so newly synced data appears
                            setTasksRefreshTrigger((prev) => prev + 1);
                        });
                    }
                })
                .catch((err) => console.error('Auto-sync error:', err));
        }
    }, [user]);

    const handleRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        // Silently sync with Google Tasks, then refresh
        try {
            const syncRes = await callOysterApi('/sync', 'POST');
            if (!syncRes.ok && syncRes.status !== 403) {
                console.warn('Sync returned', syncRes.status);
            }
        } catch (err) {
            console.error('Background sync error:', err);
        }
        setTasksRefreshTrigger((prev) => prev + 1);
        // Brief delay so the spinner is visible as feedback
        setTimeout(() => setRefreshing(false), 600);
    };

    const handleTaskStatusUpdated = useCallback((taskId, status) => {
        if (!taskId || !status) return;
        setAllTasks((prev) =>
            (prev || []).map((task) =>
                task.id === taskId
                    ? {
                          ...task,
                          status,
                          completed_date:
                              status === 'completed'
                                  ? new Date().toISOString().split('T')[0]
                                  : null,
                          completed_at:
                              status === 'completed'
                                  ? new Date().toISOString()
                                  : null
                      }
                    : task
            )
        );
    }, []);

    const handleLoading = () => {
        return (
            <div className="loading-container">
                <LoadingSpinner />
            </div>
        );
    };

    if (!user) return handleLoading();

    // Main Content Renderer based on selectedView
    const renderMainContent = () => {
        if (!selectedQuarter) return handleLoading();

        if (dashboardTasksForbidden) {
            return (
                <div
                    className="team-tasks-permission-notice"
                    style={{
                        padding: '1.5rem',
                        maxWidth: '36rem',
                        lineHeight: 1.5,
                        color: 'var(--text-muted, #6b7280)'
                    }}>
                    <p style={{ margin: 0 }}>
                        You don’t have access to the team task dashboard. You
                        can still use other features you’re allowed to open.
                    </p>
                </div>
            );
        }

        if (selectedView.type === 'all' || selectedView.type === 'team') {
            return (
                <StatsDashboard
                    viewType={selectedView.type}
                    teamId={
                        selectedView.type === 'team' ? selectedView.id : null
                    }
                    quarter={selectedQuarter}
                    weekRange={selectedWeekRange}
                    weekLabel={selectedWeekLabel}
                    allTasks={allTasks}
                    loading={loadingTasks}
                    hasManagementRights={hasManagementRights}
                    isItLeadership={isSuperAdmin}
                    currentUserEmail={user.email}
                    currentUserTeam={user.hdTeam}
                    refreshTasks={() =>
                        setTasksRefreshTrigger((prev) => prev + 1)
                    }
                    showTasks={showTasks && canSeeTasks}
                    onTaskStatusUpdated={handleTaskStatusUpdated}
                />
            );
        }

        if (selectedView.type === 'user') {
            return (
                <UserTasksView
                    key={selectedView.email}
                    userEmail={selectedView.email}
                    userName={selectedView.name}
                    quarter={selectedQuarter}
                    weekRange={selectedWeekRange}
                    weekLabel={selectedWeekLabel}
                    hasManagementRights={hasManagementRights}
                    isItLeadership={isSuperAdmin}
                    currentUserEmail={user.email}
                    currentUserTeam={user.hdTeam}
                    allTasks={allTasks}
                    loadingTasks={loadingTasks}
                    refreshTasks={() =>
                        setTasksRefreshTrigger((prev) => prev + 1)
                    }
                    showTasks={showTasks && canSeeTasks}
                    onTaskStatusUpdated={handleTaskStatusUpdated}
                />
            );
        }

        return <div>Select a view from the sidebar</div>;
    };

    return (
        <div className="team-tasks-layout">
            <div className="global-team-tasks-header">
                <div className="header-left">
                    <h2>Team Tasks Dashboard</h2>
                    <div className="header-controls">
                        <select
                            className="quarter-select"
                            value={selectedQuarter}
                            onChange={(e) =>
                                setSelectedQuarter(e.target.value)
                            }>
                            {availableQuarters.map((q) => (
                                <option key={q} value={q}>
                                    {q}
                                </option>
                            ))}
                        </select>
                        <select
                            className="quarter-select week-select"
                            value={selectedWeek}
                            onChange={(e) => setSelectedWeek(e.target.value)}>
                            <option value="all">All Weeks (Quarter)</option>
                            {availableWeeks.map((w) => (
                                <option key={w.key} value={w.key}>
                                    {w.label}
                                </option>
                            ))}
                        </select>
                        {canSeeTasks && (
                            <label className="toggle-switch-label">
                                <div className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={showTasks}
                                        onChange={(e) =>
                                            setShowTasks(e.target.checked)
                                        }
                                    />
                                    <span className="toggle-slider" />
                                </div>
                                <span className="toggle-text">Show Tasks</span>
                            </label>
                        )}
                    </div>
                </div>
                <div className="header-right">
                    <button
                        className="btn btn-primary"
                        onClick={() => setIsCreateModalOpen(true)}>
                        <FaPlus className="btn-icon" />
                        Create
                    </button>
                    {(isSuperAdmin || isTeamLead) && (
                        <Link to="/manage-users" className="btn btn-secondary">
                            <FaUsers className="btn-icon" />
                            Team Management
                        </Link>
                    )}
                    <Link
                        to={`/tasks/report?quarter=${selectedQuarter}`}
                        className="btn btn-report">
                        <FaClipboardList className="btn-icon" />
                        Task Report
                    </Link>
                    <button
                        className="btn btn-sync"
                        onClick={handleRefresh}
                        disabled={refreshing}>
                        <FaSync
                            className={`${
                                refreshing ? 'fa-spin' : ''
                            } btn-icon`}
                        />
                        {refreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            <div className="team-tasks-body">
                <Sidebar
                    sidebarData={sidebarData}
                    isLoading={isLoadingSidebar}
                    selectedView={selectedView}
                    setSelectedView={setSelectedView}
                />

                <div className="main-content-area">
                    <div className="view-title">
                        {selectedView.type === 'all' ? (
                            <div className="dwd-hero">
                                <img
                                    src="/DWD.png"
                                    alt="Darkwing Duck"
                                    className="dwd-hero-img"
                                />
                                <div className="dwd-hero-text">
                                    <h1>All Teams Dashboard</h1>
                                </div>
                            </div>
                        ) : (
                            <h1>
                                {selectedView.type === 'team' &&
                                    `${selectedView.id} Dashboard`}
                                {selectedView.type === 'user' &&
                                    `${selectedView.name}`}
                            </h1>
                        )}
                    </div>

                    <div className="content-scroll-container">
                        {renderMainContent()}
                    </div>
                </div>
            </div>

            <CreateTaskModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                currentTeam={!isSuperAdmin ? user?.hdTeam : null}
                canCreateTasks={isSuperAdmin || isTeamLead}
                defaultItemType="rock"
                onTaskCreated={() => setTasksRefreshTrigger((prev) => prev + 1)}
            />
        </div>
    );
};

export default TeamTasksPage;
